import { useEffect, useReducer, useRef, useState } from 'react';
import styled from 'styled-components';
import { Document, Paragraph, HeadingLevel, AlignmentType, Table, TableRow, Header } from 'docx';
import { HorizontalPositionRelativeFrom, HorizontalPositionAlign } from 'docx';
import { VerticalPositionRelativeFrom } from 'docx';
import { TextWrappingType, TextWrappingSide } from 'docx';
import { TableCell, ImageRun, SectionType, Packer, WidthType, convertInchesToTwip } from 'docx';
import axios from 'axios';
import { filter, find, findIndex } from 'ramda';
import moment from 'moment';
import Select from '~~elements/Select';
import Button from '~~elements/Button';
import ProgressBar from '~~elements/ProgressBar';
import { TABLET_WIDTH, PHONE_WIDTH, COLOR } from '~~elements/Const';
import DefaultAvatarJPG from '~~elements/static/DefaultAvatar.jpg';

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: 0.5rem 0 0;
  padding: 0.5rem 0;
  width: 100%;
  gap: 0.5rem;

  .print-title {
    font-size: 1.2rem;
    font-weight: bold;
  }

  .print-control {
    display: flex;
    align-items: flex-end;
    .download {
      width: 6rem;
      margin: auto auto 0 0;
    }
  }

  .print-param {
    margin: 0 0.2rem 0 0;
  }

  .print-bar {
    width: 100%;
    padding: 0.5rem 0.5rem;
    background: ${COLOR.secondary};
  }

  @media (max-width: ${TABLET_WIDTH}) {
    align-items: center;
    .print-control {
      display: flex;
      flex-direction: column;
    }

    .print-param {
      margin: 0 0 0.2rem 0;
    }
  }

  @media (max-width: ${PHONE_WIDTH}) {
    .print-control {
      .download {
        width: 100%;
      }
    }
  }
`;

const orderParamMapping = {
  name: '姓名',
  number: '背號',
  birthday: '生日',
};

const defaultPrintState = {
  rowAmount: 4,
  printType: 'INFO',
  orderParams: [
    { name: 'name', type: 'TEXT' },
    { name: 'number', type: 'TEXT' },
    { name: 'birthday', TYPE: 'DATE' },
  ],
  orderBy: { name: 'number', type: 'TEXT' },
  adesc: 'asc',
  progress: '',
  progressPercentage: 0,
  avatarWidth: 0,
  avatarHeight: 0,
};
const printReducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { ...defaultPrintState };
    case 'set':
      return { ...defaultPrintState, orderParams: [...defaultPrintState.orderParams, ...action.payload] };
    case 'avatar':
      return { ...state, avatarWidth: action.payload.width, avatarHeight: action.payload.height };
    case 'paramUpdate':
      return { ...state, ...action.payload };
    case 'progressUpdate':
      return { ...state, progress: action.payload.progress, progressPercentage: action.payload.progressPercentage };
    default:
      throw new Error();
  }
};

const tableWidth = 8500; // Twip
const tableWidthPixels = 500; // pixels
const tableCellBorders = {
  top: { size: 1 },
  right: { size: 1 },
  bottom: { size: 1 },
  left: { size: 1 },
};
const tableCellMargins = {
  top: convertInchesToTwip(0.05),
  bottom: convertInchesToTwip(0.05),
  left: convertInchesToTwip(0),
  right: convertInchesToTwip(0),
};

const ProgramPrint = ({ teamInRegistry, registry, wordingProgram = false }) => {
  const [printState, printDispatch] = useReducer(printReducer, defaultPrintState);

  const [defaultAvatarBase64, setDefaultAvatarBase64] = useState(null);
  const defaultAvatarRef = useRef(null);

  useEffect(() => {
    if (!defaultAvatarRef) {
      return;
    }

    axios
      .get(defaultAvatarRef.current.src, {
        responseType: 'arraybuffer',
      })
      .then((response) => {
        setDefaultAvatarBase64('data:;base64,' + Buffer.from(response.data, 'binary').toString('base64'));
      })
      .catch(() => {});
  }, [defaultAvatarRef]);

  useEffect(() => {
    printDispatch({
      type: 'set',
      payload: filter((col) => ['TEXT', 'DATE'].includes(col.type), registry.player_columns),
    });
  }, [registry.player_columns]);

  useEffect(() => {
    const imageWidth = tableWidthPixels / printState.rowAmount;
    const imageHeight = (imageWidth * registry.avatar_ratio_y) / registry.avatar_ratio_x;

    printDispatch({
      type: 'avatar',
      payload: {
        width: Math.floor(imageWidth),
        height: Math.floor(imageHeight),
      },
    });
  }, [printState.rowAmount, registry.avatar_ratio_y, registry.avatar_ratio_x]);

  const handleParamUpdate = (e) => {
    let value = e.target.value;
    if (e.target.name === 'orderBy') {
      value = find((param) => param.name === e.target.value, printState.orderParams);
    }
    if (e.target.name === 'rowAmount') {
      value = parseInt(value);
    }
    printDispatch({ type: 'paramUpdate', payload: { [e.target.name]: value } });
  };

  const handlePrint = () => {
    printDispatch({ type: 'progressUpdate', payload: { progress: '獲取球員資料', progressPercentage: 0 } });
    prepareImages(JSON.parse(JSON.stringify(teamInRegistry)));
  };

  const prepareImages = (printData) => {
    // return startPrint(printData);
    let total = 0;
    let count = 0;
    printDispatch({
      type: 'progressUpdate',
      payload: { progress: '獲取球員照片（此步驟比較久）', progressPercentage: 0 },
    });

    const updateProgressBar = () => {
      console.log(total, count);
      printDispatch({
        type: 'progressUpdate',
        payload: {
          progress: '獲取球員照片（此步驟比較久）',
          progressPercentage: Math.floor((total - count) / (total / 100)),
        },
      });
    };

    const avatarAccessDone = () => {
      count--;
      updateProgressBar();
      if (count > 0) {
        return;
      }
      startPrint(printData);
    };

    const avatarAccess = (player) => {
      axios
        .get(player.avatar_url_crop, {
          responseType: 'arraybuffer',
        })
        .then((response) => {
          player.avatar = 'data:;base64,' + Buffer.from(response.data, 'binary').toString('base64');
          avatarAccessDone();
        })
        .catch(() => {
          // 如果裁剪特定大小的照片讀取不到，就讀原檔
          axios
            .get(player.avatar_url, {
              responseType: 'arraybuffer',
            })
            .then((response) => {
              player.avatar = 'data:;base64,' + Buffer.from(response.data, 'binary').toString('base64');
            })
            .finally(() => {
              avatarAccessDone();
            });
        });
    };

    const imageAccess = (player, columnIndex) => {
      axios
        .get(player.columns[columnIndex].url, {
          responseType: 'arraybuffer',
        })
        .then((response) => {
          const imageBase64 = 'data:;base64,' + Buffer.from(response.data, 'binary').toString('base64');
          const image = new Image();
          image.onload = () => {
            if (image.width === 0 || image.height === 0) {
              avatarAccessDone();
              return;
            }
            const widthShrinkPercent = printState.avatarWidth / image.width;
            const heightShrinkPercent = printState.avatarHeight / image.height;
            const shrinkPercent = Math.min(widthShrinkPercent, heightShrinkPercent);
            player.columns[columnIndex].image = imageBase64;
            player.columns[columnIndex].imageWidth = Math.round(image.width * shrinkPercent);
            player.columns[columnIndex].imageHeight = Math.round(image.height * shrinkPercent);
            avatarAccessDone();
          };
          image.onerror = () => {
            avatarAccessDone();
          };
          image.src = imageBase64;
        })
        .catch(() => {
          avatarAccessDone();
        });
    };

    for (let player of printData.players) {
      if (printState.printType === 'INFO') {
        if (!player.avatar_url) {
          player.avatar = defaultAvatarBase64;
          continue;
        }

        player.avatar_url_crop = player.avatar_url.replace(
          /_[^/]*/,
          `_c${printState.avatarWidth}x${printState.avatarHeight}`,
        );

        total++;
        count++;
        updateProgressBar();
        avatarAccess(player);
        continue;
      }

      for (let column of filter((col) => ['IMAGE'].includes(col.type), registry.player_columns)) {
        const filledColumnIndex = findIndex((c) => c.name === column.name && c.type === column.type, player.columns);
        if (filledColumnIndex < 0) {
          continue;
        }
        total++;
        count++;
        updateProgressBar();
        imageAccess(player, filledColumnIndex);
      }
    }

    for (let teamStaff of registry.staff_titles) {
      const filledStaffIndex = findIndex((s) => s.title === teamStaff.title, printData.staffs);
      if (filledStaffIndex < 0) {
        continue;
      }

      if (printState.printType === 'INFO') {
        const avatarColumn = find(
          (col) => ['頭貼', 'Avatar'].includes(col.name) && col.type === 'IMAGE',
          teamStaff.columns,
        );
        if (!avatarColumn) {
          continue;
        }

        printData.staffs[filledStaffIndex].avatar = defaultAvatarBase64;
        const filledAvatarColumn = find(
          (col) => col.name === avatarColumn.name && col.type === 'IMAGE',
          printData.staffs[filledStaffIndex].columns,
        );
        if (!filledAvatarColumn) {
          continue;
        }

        printData.staffs[filledStaffIndex].avatar_url = filledAvatarColumn.url;
        printData.staffs[filledStaffIndex].avatar_url_crop = filledAvatarColumn.url.replace(
          /_.*/,
          `_c${printState.avatarWidth}x${printState.avatarHeight}`,
        );

        total++;
        count++;
        updateProgressBar();
        avatarAccess(printData.staffs[filledStaffIndex]);
        continue;
      }

      for (let column of filter(
        (col) => ['IMAGE'].includes(col.type) && !['頭貼', 'Avatar'].includes(col.name),
        teamStaff.columns,
      )) {
        const filledColumnIndex = findIndex(
          (c) => c.name === column.name && c.type === column.type,
          printData.staffs[filledStaffIndex].columns,
        );
        if (filledColumnIndex < 0) {
          continue;
        }
        total++;
        count++;
        updateProgressBar();
        imageAccess(printData.staffs[filledStaffIndex], filledColumnIndex);
      }
    }

    if (total === 0 && count === 0) {
      startPrint(printData);
    }
  };

  const startPrint = (printData) => {
    printDispatch({ type: 'progressUpdate', payload: { progress: '製作文件', progressPercentage: 0 } });
    const staffTitle = new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              text: '隨隊人員',
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: {
                before: 60,
                after: 60,
              },
            }),
          ],
          columnSpan: printState.rowAmount,
        }),
      ],
    });

    const playerTitle = new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              text: '球員名單',
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: {
                before: 60,
                after: 60,
              },
            }),
          ],
          columnSpan: printState.rowAmount,
        }),
      ],
    });

    const pages = [];
    const teamName = new Paragraph({
      text: `${printData.city} ${printData.name}`,
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
    });

    const staffRows = [];
    let staffCells = [];

    registry.staff_titles.forEach((teamStaff) => {
      const filledStaff = find((s) => s.title === teamStaff.title, printData.staffs);
      const staff = {
        avatar: null,
        name: '',
        number: '',
        columns: [],
        ...(!filledStaff ? {} : filledStaff),
      };

      const cellChildren = [];
      const staffTitle = new Paragraph({
        text: `${teamStaff.title}${staff.number && ` #${staff.number}`}`,
        heading: HeadingLevel.HEADING_6,
      });

      switch (printState.printType) {
        case 'INFO': {
          const avatarColumn = find(
            (col) => ['頭貼', 'Avatar'].includes(col.name) && col.type === 'IMAGE',
            teamStaff.columns,
          );
          if (avatarColumn && !staff.avatar) {
            staff.avatar = defaultAvatarBase64;
          }
          if (staff.avatar) {
            cellChildren.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: staff.avatar,
                    transformation: {
                      width: printState.avatarWidth,
                      height: printState.avatarHeight,
                    },
                    floating: {
                      allowOverlap: false,
                      behindDocument: false,
                      layoutInCell: true,
                      horizontalPosition: {
                        relative: HorizontalPositionRelativeFrom.MARGIN,
                        align: HorizontalPositionAlign.CENTER,
                      },
                      verticalPosition: {
                        relative: VerticalPositionRelativeFrom.MARGIN,
                        offset: 0.05 * 914400, // emus
                      },
                      wrap: {
                        type: TextWrappingType.TOP_AND_BOTTOM,
                        side: TextWrappingSide.BOTH_SIDES,
                      },
                    },
                  }),
                ],
              }),
            );
          }
          cellChildren.push(staffTitle);
          filter((col) => ['TEXT', 'DATE'].includes(col.type), teamStaff.columns).forEach((column) => {
            const filledColumn = find((c) => c.name === column.name && c.type === column.type, staff.columns);
            const display = !filledColumn
              ? ''
              : column.type === 'TEXT'
              ? filledColumn.value
              : moment(filledColumn.value).format('YYYY/MM/DD');
            cellChildren.push(new Paragraph(`${column.name}: ${display}`));
            return;
          });
          break;
        }
        case 'IMAGE': {
          cellChildren.push(staffTitle);
          filter(
            (col) => ['IMAGE'].includes(col.type) && !['頭貼', 'Avatar'].includes(col.name),
            teamStaff.columns,
          ).forEach((column) => {
            cellChildren.push(new Paragraph(`${column.name}:`));
            const filledColumn = find(
              (c) => c.name === column.name && c.type === column.type && !!c.image,
              staff.columns,
            );
            cellChildren.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: !filledColumn ? defaultAvatarBase64 : filledColumn.image,
                    transformation: {
                      width: !filledColumn ? printState.avatarWidth : filledColumn.imageWidth,
                      height: !filledColumn ? printState.avatarHeight : filledColumn.imageHeight,
                    },
                  }),
                ],
              }),
            );
            return;
          });
          break;
        }
        default:
          throw new Error();
      }

      staffCells.push(
        new TableCell({
          children: cellChildren,
          width: {
            size: Math.floor(tableWidth / printState.rowAmount),
            type: WidthType.DXA,
          },
          borders: tableCellBorders,
          margins: tableCellMargins,
        }),
      );
      if (staffCells.length === printState.rowAmount) {
        staffRows.push(
          new TableRow({
            children: [...staffCells],
            cantSplit: true,
          }),
        );
        staffCells = [];
      }
    });
    if (staffCells.length > 0) {
      staffRows.push(
        new TableRow({
          children: [
            ...staffCells,
            ...new Array(printState.rowAmount - staffCells.length).fill(
              new TableCell({ children: [new Paragraph(' ')], borders: tableCellBorders, margins: tableCellMargins }),
            ),
          ],
          cantSplit: true,
        }),
      );
    }

    const sortPlayers = (a, b) => {
      let aValue = '';
      let bValue = '';
      if (orderParamMapping[printState.orderBy.name]) {
        aValue = a[printState.orderBy.name];
        bValue = b[printState.orderBy.name];
      } else {
        aValue = a.columns[printState.orderBy.name] ? a.columns[printState.orderBy.name].value : '';
        bValue = b.columns[printState.orderBy.name] ? b.columns[printState.orderBy.name].value : '';
      }

      if (['number', '學號', '流水號'].includes(printState.orderBy.name)) {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }
      if (printState.orderBy.type === 'DATE') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (printState.adesc === 'asc') {
        return aValue > bValue ? 1 : -1;
      }

      return aValue > bValue ? -1 : 1;
    };

    const playerRows = [];
    let playerCells = [];
    const sortedPlayers = printData.players.sort(sortPlayers);
    for (let player of sortedPlayers) {
      const cellChildren = [];
      const playerName = new Paragraph({
        text: `${player.name} #${player.number}`,
        heading: HeadingLevel.HEADING_6,
      });

      switch (printState.printType) {
        case 'INFO': {
          if (player.avatar) {
            cellChildren.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: player.avatar,
                    transformation: {
                      width: printState.avatarWidth,
                      height: printState.avatarHeight,
                    },
                    floating: {
                      allowOverlap: false,
                      behindDocument: false,
                      layoutInCell: true,
                      horizontalPosition: {
                        relative: HorizontalPositionRelativeFrom.MARGIN,
                        align: HorizontalPositionAlign.CENTER,
                      },
                      verticalPosition: {
                        relative: VerticalPositionRelativeFrom.MARGIN,
                        offset: 0.05 * 914400, // emus
                      },
                      wrap: {
                        type: TextWrappingType.TOP_AND_BOTTOM,
                        side: TextWrappingSide.BOTH_SIDES,
                      },
                    },
                  }),
                ],
              }),
            );
          }
          cellChildren.push(playerName);

          const birthday = player.birthday ? moment(player.birthday).format('YYYY/MM/DD') : '';
          cellChildren.push(new Paragraph(`生日: ${birthday}`));

          const position =
            `${player.first_position === 'UNSET' ? '' : player.first_position}` +
            `, ${player.second_position === 'UNSET' ? '' : player.second_position}`;
          cellChildren.push(new Paragraph(`守位: ${position}`));

          const hands = `${player.p_hand === 'UNSET' ? '' : player.p_hand}, ${
            player.b_hand === 'UNSET' ? '' : player.b_hand
          }`;
          cellChildren.push(new Paragraph(`慣用手: ${hands}`));

          filter((col) => ['TEXT', 'DATE'].includes(col.type), registry.player_columns).forEach((column) => {
            const filledColumn = find((c) => c.name === column.name && c.type === column.type, player.columns);
            const display = !filledColumn
              ? ''
              : column.type === 'TEXT'
              ? filledColumn.value
              : moment(filledColumn.value).format('YYYY/MM/DD');
            cellChildren.push(new Paragraph(`${column.name}: ${display}`));
            return;
          });
          break;
        }
        case 'IMAGE': {
          cellChildren.push(playerName);
          filter((col) => ['IMAGE'].includes(col.type), registry.player_columns).forEach((column) => {
            cellChildren.push(new Paragraph(`${column.name}:`));
            const filledColumn = find(
              (c) => c.name === column.name && c.type === column.type && !!c.image,
              player.columns,
            );
            cellChildren.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: !filledColumn ? defaultAvatarBase64 : filledColumn.image,
                    transformation: {
                      width: !filledColumn ? printState.avatarWidth : filledColumn.imageWidth,
                      height: !filledColumn ? printState.avatarHeight : filledColumn.imageHeight,
                    },
                  }),
                ],
              }),
            );
            return;
          });
          break;
        }
        default:
          throw new Error();
      }

      playerCells.push(
        new TableCell({
          children: cellChildren,
          width: {
            size: Math.floor(tableWidth / printState.rowAmount),
            type: WidthType.DXA,
          },
          borders: tableCellBorders,
          margins: tableCellMargins,
        }),
      );
      if (playerCells.length === printState.rowAmount) {
        playerRows.push(
          new TableRow({
            children: [...playerCells],
            cantSplit: true,
          }),
        );
        playerCells = [];
      }
    }
    if (playerCells.length > 0) {
      playerRows.push(
        new TableRow({
          children: [
            ...playerCells,
            ...new Array(printState.rowAmount - playerCells.length).fill(
              new TableCell({ children: [new Paragraph(' ')], borders: tableCellBorders, margins: tableCellMargins }),
            ),
          ],
          cantSplit: true,
        }),
      );
    }

    const staffTable = new Table({
      rows: [staffTitle, ...staffRows],
      width: {
        size: tableWidth,
        type: WidthType.DXA,
      },
      alignment: AlignmentType.CENTER,
    });

    const playerTable = new Table({
      rows: [playerTitle, ...playerRows],
      width: {
        size: tableWidth,
        type: WidthType.DXA,
      },
      alignment: AlignmentType.CENTER,
    });

    const teamPage = {
      properties: {
        type: SectionType.NEXT_PAGE,
      },
      headers: {
        default: new Header({
          children: [
            new Paragraph({
              text: registry.season.title,
              alignment: AlignmentType.CENTER,
            }),
          ],
        }),
      },
      children: [teamName, staffTable, playerTable],
    };
    pages.push(teamPage);

    printDispatch({
      type: 'progressUpdate',
      payload: { progress: '製作文件', progressPercentage: 100 },
    });

    const title = `${registry.season.title}-${printData.city}_${printData.name}`;
    const doc = new Document({
      creator: 'Rebas 野革',
      title: title,
      sections: pages,
      styles: {
        default: {
          heading2: {
            run: {
              size: 26,
              color: '#000000',
            },
          },
          heading6: {
            run: {
              size: 22,
              color: '#000000',
            },
          },
        },
      },
    });

    Packer.toBuffer(doc).then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${title}-${wordingProgram ? '秩序冊' : '審核內容'}.docx`;
      link.click();

      printDispatch({
        type: 'progressUpdate',
        payload: { progress: '', progressPercentage: 0 },
      });
    });
  };

  return (
    <StyledDiv>
      <div className="print-title">列印{wordingProgram ? '秩序冊' : '審核內容'}</div>
      <div className="print-control">
        <div className="print-param">
          <Select
            label="每列球員數"
            name="rowAmount"
            value={printState.rowAmount}
            width="unset"
            onChange={handleParamUpdate}
          >
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
            <option value={6}>6</option>
          </Select>
        </div>
        <div className="print-param">
          <Select label="排序標準" name="orderBy" value={printState.orderBy.name} onChange={handleParamUpdate}>
            {printState.orderParams.map((param, i) => (
              <option key={i} value={param.name}>
                {orderParamMapping[param.name] ? orderParamMapping[param.name] : param.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="print-param">
          <Select label="排序順序" name="adesc" value={printState.adesc} onChange={handleParamUpdate}>
            <option value="desc">由大到小</option>
            <option value="asc">由小到大</option>
          </Select>
        </div>
        <div className="print-param">
          <Select label="列印內容" name="printType" value={printState.printType} onChange={handleParamUpdate}>
            <option value="INFO">報名資料</option>
            <option value="IMAGE">上傳圖檔</option>
          </Select>
        </div>
        <Button className="download" color="red" onClick={handlePrint} disabled={!!printState.progress}>
          下載
        </Button>
      </div>
      {printState.printType === 'IMAGE' && <i>建議每列球員不超過 4 避免圖片過小</i>}
      <div className="print-bar">
        <ProgressBar
          isOpen={true}
          title={printState.progress ? printState.progress : '下載'}
          completed={printState.progressPercentage}
        />
      </div>
      <img ref={defaultAvatarRef} src={DefaultAvatarJPG} alt="default-avatar" style={{ display: 'none' }} />
    </StyledDiv>
  );
};

export default ProgramPrint;
